import axios from './_axios_config';
import store from '@/state';
import router from '@/routes';
import swal from 'sweetalert2';

const customPath = (resource, ...params) => {
    const queryString = params.filter(param => param).join('&');
    return `/users/admins/${resource}${queryString ? `?${queryString}` : ''}`;
}

const isAuthenticatedRoute = (config) => {
    return config?.headers?.Authorization;
}

const processRequest = async (req) => {
    try {
        const { data, status } = await req

        if (status >= 200 && status < 300) {
            return {
                status: true,
                ...data
            }
        } else {
            throw {
                status: false,
                ...data
            }
        }

    } catch (error) {
        if (error.intercepted) {
            return {
                error: error.details,
                status: false,
                handled: true
            };
        }

        const details = error.response?.data?.details;
        const message = error.response?.data?.message;

        if (error.response?.status === 401 && isAuthenticatedRoute(error.config)) {
            store.dispatch('admin/logout');

            await swal({
                title: 'Sessão Expirada',
                text: 'Sua sessão de administrador expirou, faça login novamente',
                icon: 'warning',
                confirmButtonText: 'OK',
                customClass: {
                    confirmButton: 'btn btn-primary'
                },
                buttonsStyling: false,
                allowOutsideClick: false
            });

            router.push({ name: 'admin-login' });

            return {
                error: "Sessão expirada",
                status: false,
                handled: true
            };
        }

        if (details) {
            if (details.includes("QR Code not available yet. Wait")) {
                return {
                    error: "",
                    status: false,
                    handled: true,
                    ignore: true
                };
            }

            if (details.includes("no prefecture")) {
                return {
                    error: "Nenhuma prefeitura encontrada com esse filtro.",
                    status: false,
                    isEmpty: true,
                    handled: true
                };
            }

            if (details.includes("whatsapp session not found") || details.includes("session not initialized")) {
                return {
                    error: "Sessão do WhatsApp não encontrada. Escaneie o QR Code para conectar.",
                    status: false,
                    isEmpty: true,
                    handled: true
                };
            }
        }

        if (message) {
            if (message.includes("invalid credentials") || message.includes("Invalid credentials")) {
                return {
                    error: "E-mail ou senha incorretos",
                    status: false,
                    handled: true
                };
            }
        }

        if (error.response?.status) {
            const status = error.response.status;

            switch (status) {
                case 401:
                    return {
                        error: "E-mail ou senha incorretos",
                        status: false,
                        handled: true
                    }
                case 403:
                    return {
                        error: "Você não tem permissão para acessar este recurso",
                        status: false,
                        handled: true
                    }
                // case 404:
                //     return {
                //         error: "Nenhum dado foi encontrado",
                //         status: false,
                //         handled: true
                //     }
                case 400:
                    return {
                        error: error.response.data?.message || "Dados inválidos. Verifique os campos",
                        status: false,
                        handled: true
                    }
                case 500:
                    return {
                        error: "Erro no servidor. Tente novamente mais tarde",
                        status: false,
                        handled: true
                    }
                default:
                    return {
                        error: "Poxa, algo deu errado. Tente novamente, se o problema persistir, contate o suporte",
                        status: false,
                        handled: true
                    };
            }
        }

        if (error.status) {
            switch (error.status) {
                case 401:
                    return {
                        error: "E-mail ou senha incorretos",
                        status: false,
                        handled: true
                    }
                case 403:
                    return {
                        error: "Você não tem permissão para acessar este recurso",
                        status: false,
                        handled: true
                    }
                // case 404:
                //     return {
                //         error: "Nenhum dado foi encontrado",
                //         status: false,
                //         handled: true
                //     }
                default:
                    return {
                        error: "Poxa, algo deu errado. Tente novamente, se o problema persistir, contate o suporte",
                        status: false,
                        handled: true
                    };
            }
        }
    }

    return {
        error: "Algo deu errado. Tente novamente",
        status: false,
        handled: true
    }
}

// só parâmetros com valor: busca vazia ou filtro desligado não vão pra URL
const analyticsParams = (params) => Object.entries(params)
    .filter(([, value]) => value !== '' && value !== null && value !== undefined && value !== false)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`);

export const ct_admins = {
    async signIn(payload) {
        try {
            const responseFiltered = await processRequest(
                axios.post(customPath('signin'), payload)
            );

            if (!responseFiltered.status) {
                throw responseFiltered.error
            }

            return responseFiltered;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async getPrefectureAccounts(token, status = 'pending', page = 1, limit = 10) {
        try {
            const success = await processRequest(
                axios.get(customPath('prefecture', `status=${status}`, `page=${page}`, `limit=${limit}`), {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                throw success.error
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async updatePrefectureStatus(token, id, payload) {
        try {
            const success = await processRequest(
                axios.post(customPath(`prefecture/${id}`), payload, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                throw success.error
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async getWhatsappInfo(token) {
        try {
            const success = await processRequest(
                axios.get(customPath('whatsapp'), {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                if (success.ignore) return success;

                throw success.error
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async restartWhatsapp(token) {
        try {
            const success = await processRequest(
                axios.put(customPath('whatsapp/restart'), {}, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                throw success.error
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async getWhatsappQrCode(token) {
        try {
            const response = await axios.get(customPath('whatsapp-qr'), {
                headers: {
                    Authorization: `Bearer ${token}`
                },
                responseType: 'blob'
            });

            const url = URL.createObjectURL(response.data);

            return {
                status: true,
                url
            };
        } catch (error) {
            return {
                error: "Não foi possível carregar o QR Code",
                status: false
            }
        }
    },

    async getTenants(token, { page = 1, limit = 10, search = '' } = {}) {
        try {
            const success = await processRequest(
                axios.get(customPath('tenants', `page=${page}`, `limit=${limit}`, search && `search=${encodeURIComponent(search)}`), {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                throw success.error;
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async grantTenantTrial(token, tenant_id, { days, reason } = {}) {
        try {
            const success = await processRequest(
                axios.put(customPath(`tenants/${tenant_id}/trial`), { days, reason }, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                throw success.error;
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async getTenantTrials(token, { page = 1, limit = 10, active = false } = {}) {
        try {
            const success = await processRequest(
                axios.get(customPath('tenants/trials', `page=${page}`, `limit=${limit}`, active && 'active=true'), {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                throw success.error;
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async revokeTenantTrial(token, tenant_id) {
        try {
            const success = await processRequest(
                axios.delete(customPath(`tenants/${tenant_id}/trial`), {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                throw success.error;
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },
    
    async getAnalytics(token, resource, params = {}) {
        try {
            const success = await processRequest(
                axios.get(customPath(`analytics/${resource}`, ...analyticsParams(params)), {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                throw success.error;
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    // o token vai no header, então o CSV é baixado como blob em vez de <a href>
    async exportAnalyticsCsv(token, resource, params = {}) {
        try {
            const response = await axios.get(
                customPath(`analytics/${resource}`, ...analyticsParams({ ...params, format: 'csv' })),
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    responseType: 'blob'
                }
            );

            const disposition = response.headers['content-disposition'] || '';
            const filename = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? `${resource}.csv`;

            return { status: true, blob: response.data, filename };
        } catch (error) {
            if (error.response?.status === 401) {
                store.dispatch('admin/logout');
                router.push({ name: 'admin-login' });
                return { error: 'Sessão expirada', status: false };
            }

            // erros de validação continuam vindo em JSON, mas aqui chegam como blob
            let message = 'Não foi possível exportar';
            try {
                const body = JSON.parse(await error.response.data.text());
                message = body.details || body.message || message;
            } catch {
                // resposta sem corpo legível: fica a mensagem genérica
            }

            return { error: message, status: false };
        }
    },

    async getAllCodesVerify(token) {
        try {
            const success = await processRequest(
                axios.get(customPath(`codes_verify`), {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                throw success.error;
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async getUsers(token, { page = 1, limit = 10, search = '', role = '' } = {}) {
        try {
            const success = await processRequest(
                axios.get(customPath('users', `page=${page}`, `limit=${limit}`, search && `search=${encodeURIComponent(search)}`, role && `role=${role}`), {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                throw success.error;
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async getSuggestions(token, params = {}) {
        try {
            const success = await processRequest(
                axios.get(customPath('suggestions', ...analyticsParams(params)), {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                throw success.error;
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    async updateSuggestion(token, suggestion_id, payload) {
        try {
            const success = await processRequest(
                axios.put(customPath(`suggestions/${suggestion_id}`), payload, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
            );

            if (!success.status) {
                throw success.error;
            }

            return success;
        } catch (error) {
            return {
                error,
                status: false
            }
        }
    },

    // mesmo caso das análises: o token vai no header, então o CSV desce como blob
    async exportSuggestionsCsv(token, params = {}) {
        try {
            const response = await axios.get(
                customPath('suggestions', ...analyticsParams({ ...params, format: 'csv' })),
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    responseType: 'blob'
                }
            );

            const disposition = response.headers['content-disposition'] || '';
            const filename = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? 'sugestoes.csv';

            return { status: true, blob: response.data, filename };
        } catch (error) {
            if (error.response?.status === 401) {
                store.dispatch('admin/logout');
                router.push({ name: 'admin-login' });
                return { error: 'Sessão expirada', status: false };
            }

            let message = 'Não foi possível exportar';
            try {
                const body = JSON.parse(await error.response.data.text());
                message = body.details || body.message || message;
            } catch {
                // resposta sem corpo legível: fica a mensagem genérica
            }

            return { error: message, status: false };
        }
    }
}